import clsx from 'clsx';
import { forwardRef, Key, useCallback, useMemo, useState } from 'react';
import { Autocomplete, Button, Select } from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import IconButton from '~/components/buttons/NewIconButton';
import Icon from '~/components/data-display/Icon';
import { SkeletonLoader } from '~/components/data-display/SkeletonLoader/SkeletonLoader';
import { CounterpartComboBoxFooter } from '~/components/inputs/Select/CounterpartComboBox';
import { ListBoxItems, ListBoxPopoverContainer } from '~/components/inputs/Select/ListBoxPopover';
import SearchField from '~/components/inputs/TextField/SearchField';
import IconChevronDown from '~/icons/icon-chevron-down.svg?react';
import IconClose from '~/icons/icon-close.svg?react';
import { useCounterpart, useCounterpartsSearch } from '~/services/counterparts';
import { Counterpart } from '~/types/counterparts';
import styles from './BookkeepingCounterpartField.module.scss';

interface BookkeepingCounterpartFieldProps {
  autoFocus?: boolean;
  invalid: boolean;
  onChange: (value: Counterpart | null) => void;
  readOnly?: boolean;
  value: { id: string; companyName: string | null } | null;
  isLoading?: boolean;
}

const checkCounterpartMatch = (searchText: string) => (counterpart: Counterpart) => {
  return (counterpart.companyName ?? '').toLowerCase().includes(searchText.toLowerCase());
};

const BookkeepingCounterpartField = forwardRef<HTMLDivElement, BookkeepingCounterpartFieldProps>(
  ({ autoFocus, invalid, onChange, readOnly, value, isLoading }, ref) => {
    const { t } = useTranslation('bookkeeping', { keyPrefix: 'inputs.counterpart_field' });
    const [textInput, setTextInput] = useState<string>('');
    const { data: counterpartsQuery } = useCounterpartsSearch(textInput);
    const { data: resolvedCounterpart } = useCounterpart(value?.companyName == null ? value?.id : null);
    const displayName = value?.companyName ?? resolvedCounterpart?.companyName ?? '';

    const counterparts = useMemo(() => {
      return counterpartsQuery?.pages.flatMap((page) => page.counterparts) ?? [];
    }, [counterpartsQuery]);

    const options = useMemo(
      () =>
        counterparts
          .filter(checkCounterpartMatch(textInput))
          .slice(0, 200)
          .map((counterpart) => ({
            id: counterpart.id,
            label: counterpart.companyName ?? '',
            description: counterpart.companyName ?? '',
            textValue: counterpart.companyName ?? '',
          })),
      [counterparts, textInput],
    );

    const handleChange = useCallback(
      (id: Key | null) => {
        const counterpart = counterparts.find((c) => c.id === id);
        onChange(counterpart ?? null);
        setTextInput('');
      },
      [counterparts, onChange],
    );

    const handleCreatedCounterpartChange = useCallback(
      (counterpart: Counterpart) => {
        onChange(counterpart);
        setTextInput('');
      },
      [onChange],
    );

    return (
      <div className={styles.container}>
        <Select
          autoFocus={autoFocus}
          aria-label={t('label', { defaultValue: 'Counterpart' })}
          className={styles.combobox}
          isDisabled={readOnly}
          isInvalid={invalid}
          onChange={handleChange}
          ref={ref}
          value={value?.id ?? null}
        >
          <Button className={styles.button}>
            {isLoading ? (
              <BookkeepingCounterpartSkeleton />
            ) : (
              <div className={clsx(styles.subfield, styles.description)}>
                <div className={styles.label}>{displayName}</div>
                {!value && !readOnly && <Icon component={IconChevronDown} size="sm" className={styles.chevron} />}
              </div>
            )}
          </Button>

          <ListBoxPopoverContainer
            additionalContent={
              <CounterpartComboBoxFooter
                searchText={textInput}
                hasCreateButton
                hasMergeCallToAction
                onChange={handleCreatedCounterpartChange}
              />
            }
            additionalContentSticky
            placement="bottom start"
            shouldFlip={false}
          >
            <Autocomplete>
              <SearchField
                autofocus
                onChange={setTextInput}
                label={t('label')}
                placeholder={t('search')}
                hideLabel
                className={styles.searchField}
                value={textInput}
              />

              <ListBoxItems options={options} />
            </Autocomplete>
          </ListBoxPopoverContainer>
        </Select>
        {value && !readOnly && (
          <IconButton
            Icon={IconClose}
            size="small"
            variant="ghost"
            className={styles.clearButton}
            onClick={() => onChange(null)}
          />
        )}
      </div>
    );
  },
);

const BookkeepingCounterpartSkeleton = () => {
  return (
    <>
      <div className={styles.skeletonSubfield}>
        <SkeletonLoader count={2} height="18px" className={styles.skeleton} />
      </div>
    </>
  );
};

export default BookkeepingCounterpartField;
